/** Stat counters an achievement can track (columns of user_stats). */
export type AchievementMetric =
  | 'kissesGiven'
  | 'kissesReceived'
  | 'mutualKisses'
  | 'spins'
  | 'roundsPlayed'
  | 'giftsSent'
  | 'giftsReceived'
  | 'djPlays'
  | 'messagesSent'
  | 'stickersSent'
  | 'wheelSpins'
  | 'admirationsMade'
  | 'admirationsReceived'
  | 'leagueChests'
  | 'bestLeague'
  | 'bottlesChanged';

export interface AchievementLevel {
  /** Metric value that earns this star. */
  at: number;
  /** Hearts credited once when the star is earned. */
  reward: number;
  /** Unlock code stored per player (single-level achievements of earlier versions keep their codes). */
  code: string;
}

export interface AchievementDef {
  code: string;
  icon: string;
  metric: AchievementMetric;
  /** One star per level (reference: medals with 3–5 star levels). */
  levels: readonly AchievementLevel[];
}

const REWARDS = [5, 10, 20, 40, 80];

/** Levels `at[i]` with growing rewards; `legacy` maps a level index to a code unlocked by an earlier version. */
function def(
  code: string,
  icon: string,
  metric: AchievementMetric,
  at: number[],
  legacy: Record<number, string> = {},
): AchievementDef {
  return {
    code,
    icon,
    metric,
    levels: at.map((value, i) => ({ at: value, reward: REWARDS[i]!, code: legacy[i] ?? `${code}:${i + 1}` })),
  };
}

/** Titles/descriptions live in the i18n catalogs under `achievement.<code>.title|desc` ({count} = next goal). */
export const ACHIEVEMENTS: readonly AchievementDef[] = [
  def('kisser', '💋', 'kissesGiven', [1, 10, 50, 200, 500], {
    0: 'first_kiss',
    2: 'kisser_50',
    4: 'kisser_500',
  }),
  def('popular', '🌟', 'kissesReceived', [10, 50, 100, 500, 1000], {
    0: 'popular_10',
    2: 'popular_100',
    4: 'popular_1000',
  }),
  def('mutual', '💑', 'mutualKisses', [1, 10, 50, 200], { 1: 'mutual_10' }),
  def('spinner', '🍾', 'spins', [25, 100, 500, 2000], { 0: 'spinner_25' }),
  def('regular', '🎯', 'roundsPlayed', [10, 100, 500, 2000], { 1: 'regular_100' }),
  def('generous', '🎁', 'giftsSent', [1, 10, 50, 200, 1000], { 1: 'generous_10' }),
  def('adored', '🌹', 'giftsReceived', [1, 10, 50, 200, 1000], { 1: 'adored_10' }),
  def('dj', '🎧', 'djPlays', [1, 5, 25, 100], { 0: 'dj_1', 2: 'dj_25' }),
  def('chatty', '💬', 'messagesSent', [10, 100, 500, 2000], { 1: 'chatty_100' }),
  def('emotional', '🧩', 'stickersSent', [1, 25, 100, 500]),
  def('lucky', '🎡', 'wheelSpins', [1, 10, 50, 200, 1000]),
  def('admirer', '😍', 'admirationsMade', [1, 10, 50, 200]),
  def('charmer', '💘', 'admirationsReceived', [1, 10, 50]),
  def('champion', '🧰', 'leagueChests', [1, 5, 20, 50]),
  def('league', '💎', 'bestLeague', [1, 2, 3, 4, 5]),
  def('bartender', '🍸', 'bottlesChanged', [1, 10, 50]),
];

/** All stars there are to earn. */
export const ACHIEVEMENT_STARS = ACHIEVEMENTS.reduce((sum, a) => sum + a.levels.length, 0);

export type StatsSnapshot = Record<AchievementMetric, number>;

/** Levels newly reached by `stats` whose codes are not in `unlocked` yet. */
export function newlyUnlocked(
  stats: StatsSnapshot,
  unlocked: ReadonlySet<string>,
): { achievement: AchievementDef; level: number; def: AchievementLevel }[] {
  return ACHIEVEMENTS.flatMap((a) =>
    a.levels.flatMap((l, i) =>
      !unlocked.has(l.code) && stats[a.metric] >= l.at ? [{ achievement: a, level: i + 1, def: l }] : [],
    ),
  );
}
